import { SECTION_SLUGS } from "./constants/sections";
import { FIELD_KEYS } from "./constants/fields";
import { BENEFICIARIES_SLUG } from "./essentials-spec";

/**
 * s227 (BOX-FITS-1, D10, Reade: "b") — THE LONG-ANSWER LIMIT. ONE declaration, read by the writing box (its counter
 * and maxLength) and by the server (which refuses a longer value on every path that writes one of these fields).
 *
 * The board draws the counter on the three long-answer panels as "N / 600" (Main.dc.html:1359,
 * `count: filled ? '128 / 600' : '0 / 600'`). Rule 19 ("no invented numbers") is why the limit had to be REAL before
 * the counter could show it: a count the server does not enforce is a promise nothing keeps.
 *
 * Measured before the limit shipped (s227): 0 of 102 production answers and 0 of 104 staging answers in these three
 * fields were over 600 (the longest was 34 characters), so the limit refuses nothing that already exists.
 *
 * Length is counted the same way on both sides — JavaScript string length — so the box and the server agree.
 */
export const LONG_ANSWER_MAX = 600;

export const LONG_ANSWER_FIELDS: readonly { sectionSlug: string; fieldKey: string }[] = [
  // daily — "Health" and "Their day" (the same rows the kids screen writes, rule 121)
  { sectionSlug: SECTION_SLUGS.CHILDREN_DEPENDENTS_OVERVIEW, fieldKey: FIELD_KEYS.CONDITIONS },
  { sectionSlug: SECTION_SLUGS.CHILDREN_DEPENDENTS_OVERVIEW, fieldKey: FIELD_KEYS.ROUTINES },
  // ids — "Where is it kept?"
  { sectionSlug: SECTION_SLUGS.PERSONAL_INFORMATION_GOVERNMENT_IDS, fieldKey: FIELD_KEYS.LOCATION },
  // s237 (D-237.30, rule 19): every row's NOTE opens this same box, so its counter is real here too. Measured before the
  // limit (s237 note check, both databases): 0 notes over 600 characters in any note column or cell; the longest is 459.
  // The 11 active note fields of the sections the interview's row tables save to (chapters one and two).
  { sectionSlug: BENEFICIARIES_SLUG, fieldKey: FIELD_KEYS.NOTES },
  { sectionSlug: SECTION_SLUGS.CHILDREN_DEPENDENTS_OVERVIEW, fieldKey: FIELD_KEYS.IDENTITY_NOTES },
  { sectionSlug: SECTION_SLUGS.CHILDREN_DEPENDENTS_OVERVIEW, fieldKey: FIELD_KEYS.CARE_NOTES },
  { sectionSlug: SECTION_SLUGS.LEGAL_YOUR_EXECUTOR, fieldKey: FIELD_KEYS.NOTES },
  { sectionSlug: SECTION_SLUGS.PERSONAL_INFORMATION_GOVERNMENT_IDS, fieldKey: FIELD_KEYS.NOTES },
  { sectionSlug: SECTION_SLUGS.FAMILY_PETS, fieldKey: FIELD_KEYS.NOTES },
  { sectionSlug: SECTION_SLUGS.ASSETS_PROPERTIES, fieldKey: FIELD_KEYS.NOTES },
  { sectionSlug: SECTION_SLUGS.FINANCIAL_AND_INSURANCE_HOUSING, fieldKey: FIELD_KEYS.NOTES },
  { sectionSlug: SECTION_SLUGS.ASSETS_VEHICLE_LIST, fieldKey: FIELD_KEYS.NOTES },
  { sectionSlug: SECTION_SLUGS.ASSETS_VALUABLES, fieldKey: FIELD_KEYS.NOTES },
  { sectionSlug: SECTION_SLUGS.ASSETS_SAFES_LOCKED_PLACES, fieldKey: FIELD_KEYS.NOTES },
  // s237 chapter three PR 2 (rule 19): Work's "Your role" is a long answer (the board's `long`, the writing space's counter).
  // Measured first: 0 roles over 600 on either database, the longest 30.
  { sectionSlug: SECTION_SLUGS.FINANCIAL_BUSINESS_SELF_EMPLOYMENT, fieldKey: FIELD_KEYS.ROLE },
];

export function isLongAnswerField(sectionSlug: string, fieldKey: string): boolean {
  return LONG_ANSWER_FIELDS.some((f) => f.sectionSlug === sectionSlug && f.fieldKey === fieldKey);
}

/** Over the limit? `null`/`undefined` never are. Pure. */
export function exceedsLongAnswer(value: string | null | undefined): boolean {
  return typeof value === "string" && value.length > LONG_ANSWER_MAX;
}

/** The board's counter text, "N / 600". Pure. */
export function longAnswerCount(value: string): string {
  return `${value.length} / ${LONG_ANSWER_MAX}`;
}

/** What the server says when it refuses (a 400). The box stops at the limit, so a planner should never see it. */
export const LONG_ANSWER_TOO_LONG = `This answer is longer than ${LONG_ANSWER_MAX} characters. Please shorten it and save again.`;
